import { arrayMove } from '@dnd-kit/sortable';
export const ROOT_ID = 'root';
export const MIN_DEPTH = 0;
export const INDENTATION_WIDTH = 32;
export const flattenTree = (items, parentId = null, depth = MIN_DEPTH) => {
    return items.reduce((acc, item, index) => {
        const flatItem = {
            id: item.id,
            item,
            parentId,
            depth,
            index,
            children: [],
        };
        const result = [flatItem];
        // Flatten children if they exist
        if (item.children &&
            Array.isArray(item.children) &&
            item.children.length > 0) {
            result.push(...flattenTree(item.children, item.id, depth + 1));
        }
        return [...acc, ...result];
    }, []);
};
export const buildTree = (flattenedItems) => {
    if (!Array.isArray(flattenedItems) || flattenedItems.length === 0) {
        return [];
    }
    const root = {
        id: ROOT_ID,
        name: ROOT_ID,
        parentId: '',
        path: '',
        children: [],
    };
    const nodeMap = new Map();
    nodeMap.set(ROOT_ID, root);
    // Create all nodes first
    const processedItems = flattenedItems.map((flatItem) => {
        const item = {
            ...flatItem.item,
            children: [],
        };
        nodeMap.set(item.id, item);
        return { ...flatItem, item };
    });
    // Build relationships
    for (const flatItem of processedItems) {
        const { item } = flatItem;
        const parentId = flatItem.parentId ?? ROOT_ID;
        const parent = nodeMap.get(parentId);
        if (parent && parent.children) {
            parent.children.push(item);
        }
    }
    return root.children || [];
};
export const removeChildrenOf = (items, parentIds) => {
    const excludeSet = new Set(parentIds);
    return items.filter((item) => {
        // if this item's parent is in the exclude set, exclude this item too
        if (item.parentId && excludeSet.has(item.parentId)) {
            // also add this item to the exclude set so its children get filtered out
            excludeSet.add(item.id);
            return false;
        }
        return true;
    });
};
const calculateDragDepth = (offset, indentationWidth) => {
    return indentationWidth <= 0 ? 0 : Math.round(offset / indentationWidth);
};
const getMaxDepth = (prevItem) => {
    return prevItem ? prevItem.depth + 1 : MIN_DEPTH;
};
const getMinDepth = (nextItem) => {
    return nextItem ? nextItem.depth : MIN_DEPTH;
};
export const getProjection = (items, activeId, overId, dragOffset, indentationWidth) => {
    const overItemIndex = items.findIndex(({ id }) => id === overId);
    const activeItemIndex = items.findIndex(({ id }) => id === activeId);
    if (overItemIndex === -1 || activeItemIndex === -1) {
        return null;
    }
    const activeItem = items[activeItemIndex];
    if (!activeItem) {
        return null;
    }
    const newItems = arrayMove([...items], activeItemIndex, overItemIndex);
    const previousItem = newItems[overItemIndex - 1];
    const nextItem = newItems[overItemIndex + 1];
    const dragDepth = calculateDragDepth(dragOffset, indentationWidth);
    const projectedDepth = activeItem.depth + dragDepth;
    const maxDepth = getMaxDepth(previousItem);
    const minDepth = getMinDepth(nextItem);
    const depth = Math.max(minDepth, Math.min(maxDepth, projectedDepth));
    const getParentId = () => {
        if (depth === MIN_DEPTH || !previousItem) {
            return null;
        }
        if (depth === previousItem.depth) {
            return previousItem.parentId;
        }
        if (depth > previousItem.depth) {
            return previousItem.id;
        }
        const newParent = newItems
            .slice(0, overItemIndex)
            .reverse()
            .find((item) => item.depth === depth);
        return newParent?.parentId ?? null;
    };
    return {
        depth,
        maxDepth,
        minDepth,
        parentId: getParentId(),
    };
};
export const getTreeFromFlat = ({ flatData, getKey = (node) => node.id, getParentKey = (node) => node.parentId, rootKey = '0', }) => {
    if (!flatData) {
        return [];
    }
    const childrenToParents = {};
    flatData.forEach((child) => {
        const parentKey = getParentKey(child);
        if (parentKey in childrenToParents) {
            childrenToParents[parentKey].push(child);
        }
        else {
            childrenToParents[parentKey] = [child];
        }
    });
    if (!(rootKey in childrenToParents)) {
        return [];
    }
    const trav = (parent) => {
        const parentKey = getKey(parent);
        if (parentKey in childrenToParents) {
            return {
                ...parent,
                children: childrenToParents[parentKey].map((child) => trav(child)),
            };
        }
        return { ...parent };
    };
    return childrenToParents[rootKey].map((child) => trav(child));
};
function walkDescendants({ callback, getNodeKey, ignoreCollapsed, isPseudoRoot = false, node, parentNode = null, currentIndex, path = [], lowerSiblingCounts = [], }) {
    // The pseudo-root is not considered in the path
    const selfPath = isPseudoRoot
        ? []
        : [...path, getNodeKey({ node, treeIndex: currentIndex })];
    const selfInfo = isPseudoRoot
        ? null
        : {
            node,
            parentNode,
            path: selfPath,
            lowerSiblingCounts,
            treeIndex: currentIndex,
        };
    if (!isPseudoRoot) {
        const callbackResult = callback(selfInfo);
        // Cut walk short if the callback returned false
        if (callbackResult === false) {
            return false;
        }
    }
    // Return self on nodes with no children or hidden children
    if (!node.children ||
        (node.expanded !== true && ignoreCollapsed && !isPseudoRoot)) {
        return currentIndex;
    }
    // Get all descendants
    let childIndex = currentIndex;
    const childCount = node.children.length;
    if (typeof node.children !== 'function') {
        for (let i = 0; i < childCount; i += 1) {
            childIndex = walkDescendants({
                callback,
                getNodeKey,
                ignoreCollapsed,
                node: node.children[i],
                parentNode: isPseudoRoot ? null : node,
                currentIndex: childIndex + 1,
                lowerSiblingCounts: [...lowerSiblingCounts, childCount - i - 1],
                path: selfPath,
            });
            // Cut walk short if the callback returned false
            if (childIndex === false) {
                return false;
            }
        }
    }
    return childIndex;
}
export function walk({ treeData, getNodeKey, callback, ignoreCollapsed = true, }) {
    if (!treeData || treeData.length < 1) {
        return;
    }
    walkDescendants({
        callback,
        getNodeKey,
        ignoreCollapsed,
        isPseudoRoot: true,
        node: { children: treeData },
        currentIndex: -1,
        path: [],
        lowerSiblingCounts: [],
    });
}
export function getFlatFromTree({ treeData, getNodeKey, ignoreCollapsed = true, }) {
    if (!treeData || treeData.length < 1) {
        return [];
    }
    const flattened = [];
    walk({
        treeData,
        getNodeKey,
        ignoreCollapsed,
        callback: (nodeInfo) => {
            flattened.push(nodeInfo);
        },
    });
    return flattened;
}
